import { Search, Close } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Stack,
  styled,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { TextButton } from '@shared/components';
import { forwardRef } from 'react';

const StyledInput = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<Pick<SearchInputFieldProps, 'variant'>>(({ theme, variant }) => ({
  ...theme.typography.body3,
  '& input': {
    '&::placeholder': {
      color: theme.vars.palette.text.disabled,
      ...theme.typography.body2,
      fontWeight: 450,
      opacity: 1,
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: `1.5px solid ${theme.vars.palette.grey.A100}` },

    '& .MuiInputAdornment-positionStart': {
      color: theme.vars.palette.grey[400],
    },
  },
  margin: 0,
}));

export interface SearchInputFieldProps
  extends Pick<
    TextFieldProps,
    'id' | 'onChange' | 'value' | 'margin' | 'placeholder' | 'fullWidth'
  > {
  onReset?: () => void;
  onSubmit?: () => void;
  variant?: 'filled' | 'outlined';
}

const SearchInputField = forwardRef(
  (
    {
      margin = 'normal',
      variant = 'outlined',
      fullWidth = false,
      placeholder,
      onReset,
      onSubmit,
      value,
      ...rest
    }: SearchInputFieldProps,
    ref
  ) => {
    const t = useProductsTFunction();
    return (
      <StyledInput
        variant={variant}
        value={value}
        {...rest}
        inputRef={ref}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="disabled" />
            </InputAdornment>
          ),
          endAdornment: (
            <Stack flexDirection="row">
              {onSubmit && (
                <TextButton onClick={onSubmit}>{t('products.search.openSearch.search')}</TextButton>
              )}
              <IconButton
                onClick={onReset}
                edge="end"
                style={{ visibility: value && onReset ? 'visible' : 'hidden' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ),
        }}
        margin={margin}
        size="small"
        autoComplete="off"
        fullWidth={fullWidth}
        placeholder={placeholder}
      />
    );
  }
);

export default SearchInputField;
